import { flatten, forEach, get, map, omit, unset } from 'lodash';
import { useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';

import { handleApiErrors } from '../../../api/axiosInstance';
import { updateCompanyMileagePurpose } from '../../../api/company';
import { updateCompanySettings } from '../../../api/company-settings';
import Toast from '../../../components/Toast';
import useModal from '../../../hooks/useModal';
import { saveCompany, updateCompanySettingsStore } from '../../../stores/actions/common';
import { hasSystemAdminRole } from '../../../utils/roles';
import { REIMBURSEMENT_PURPOSE, TAX_RETURN_PURPOSE } from './company-settings-constants';
import useCommuteFormState from './useCommuteFormState';

const useCompanySettingsState = ({ t, form }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const [
    isCommuteConfirmModalVisible,
    openCommuteConfirmModal,
    closeCommuteConfirmModal,
  ] = useModal();
  const [isDirty, setIsDirty] = useState(false);

  const authUser = useSelector(store => store.profile);
  const { currentCompany } = useSelector(store => store.common);
  const companySettings = currentCompany.companySettingId || {};
  const [currentCompanySettings, setCurrentCompanySettings] = useState(
    currentCompany.companySettingId?.settings || {},
  );
  const [currentCompanySettingValues, setCurrentCompanySettingValues] = useState(
    currentCompany.companySettingId?.values || {},
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    commuteSettings: companyCommuteSettings,
    formattedCommuteSettings: formattedCompanyCommuteSettings,
    isCommuteSettingsDirty,
    handleCommuteSettingChange,
    handleResetCommuteDirty,

    isCommuteSettingSingleLocation,
    isCommuteSettingNotHaveOffice,
    isCommuteSettingCustom,
    isNoOfficeMaxDeduction,
    isHomeAddressMaxCommuteDeduction,
    commuteDistanceUnit,
  } = useCommuteFormState({
    initialCommuteSettings: currentCompany.companySettingId?.commute || {},
  });

  const handleActiveFeatureChange = (setting = {}) => {
    setIsDirty(true);
    setCurrentCompanySettings(state => ({ ...state, ...setting }));
  };

  const handleSettingValueChange = (setting = {}) => {
    setIsDirty(true);
    form.setFieldsValue({ ...currentCompanySettingValues, ...setting });
    setCurrentCompanySettingValues({ ...currentCompanySettingValues, ...setting });

    if (setting.mileagePurpose === REIMBURSEMENT_PURPOSE) {
      handleActiveFeatureChange({
        tripApprovalWorkflow: true,
        approveReceipt: true,
        createReceipt: true,
      });
    } else if (setting.mileagePurpose === TAX_RETURN_PURPOSE) {
      handleActiveFeatureChange({
        tripApprovalWorkflow: false,
        approveReceipt: false,
        createReceipt: false,
      });
    }
  };

  const handleMileagePurposeUpdate = useCallback(
    async purpose => {
      if (purpose && currentCompany.mileagePurpose !== purpose && hasSystemAdminRole(authUser)) {
        await updateCompanyMileagePurpose(currentCompany._id, purpose);

        // Update currentCompany in store
        dispatch(saveCompany({ mileagePurpose: purpose }));
      }
    },
    [dispatch, authUser, currentCompany.mileagePurpose, currentCompany._id],
  );

  const handleFormSave = useCallback(async () => {
    closeCommuteConfirmModal();
    setIsSubmitting(true);

    try {
      await handleMileagePurposeUpdate(currentCompanySettingValues.mileagePurpose);
    } catch (error) {
      setIsSubmitting(false);
      return handleApiErrors(error.response, () => {
        Toast({
          type: 'error',
          message: t('errorUpdatingMileagePurpose'),
        });
      });
    }

    const params = {};

    params.commute = formattedCompanyCommuteSettings;

    const ignoredValues = [
      'ach_enabled',
      'mileagePurpose',
      'billingMethod',
      'geofencePrevTripDistance',
      'geofenceDistanceUnit',
      'geofenceDistance',
    ];
    if (!hasSystemAdminRole(authUser)) {
      ignoredValues.push('stripe');
    }
    params.values = omit(currentCompanySettingValues, ignoredValues);

    if (!currentCompanySettings?.enforceTripExpirationPolicy) {
      params.values = omit(params.values, ['enforceTripExpirationDays']);
    }

    if (hasSystemAdminRole(authUser)) {
      if (!params.values?.stripe?.invoiceDaysUntilDue) {
        setIsSubmitting(false);
        return Toast({
          type: 'error',
          message: t('invoiceDaysUntilDueMustBeHigher'),
        });
      }

      params.settings = Object.keys(currentCompanySettings).map(key => ({
        name: key,
        value: currentCompanySettings[key],
      }));

      // Check if duplicate invoices are enabled for the company
      if (!currentCompanySettings?.duplicateInvoice) {
        // If not enabled, remove default and duplicate invoices from the parameters
        params.values = omit(params.values, [
          'stripe.customFields.defaultInvoice',
          'stripe.customFields.duplicateInvoices',
        ]);
      } else {
        // Retrieve the total number of rates from the query client
        const totalRatesQty =
          queryClient.getQueryData(['fetchCompanyRates', currentCompany._id])?.documents?.length ||
          0;

        // Extract duplicate invoices from the params values
        const dupInvoices = get(params.values, 'stripe.customFields.duplicateInvoices', []);
        // Flatten all assigned rates including default invoice and duplicate invoices
        const flattenAssignedRates = flatten([
          ...get(params.values, 'stripe.customFields.defaultInvoice.productsIds'),
          ...map(dupInvoices).map(invoice => get(invoice, 'productsIds', [])),
        ]);

        // Check if any duplicate invoice has no product IDs assigned
        const hasNoProductIds = dupInvoices.some(invoice => !invoice?.productsIds?.length);
        // If any duplicate invoice lacks product IDs, stop the submission process and display an error message
        if (hasNoProductIds) {
          setIsSubmitting(false);
          Toast({
            type: 'error',
            message: t('duplicateInvoiceSettings'),
            description: t('eachDuplicateInvoiceMustHaveAtLeastOneProduct'),
          });
          return;
        }

        // Check if all rates are assigned to an invoice
        if (flattenAssignedRates.length !== totalRatesQty) {
          setIsSubmitting(false);
          Toast({
            type: 'error',
            message: t('duplicateInvoiceSettings'),
            description: t('youMustAssignAllRatesToInvoice'),
          });
          return;
        }

        // Remove IDs from duplicate invoices and their fields
        forEach(dupInvoices, (inv, i) => {
          unset(params.values, `stripe.customFields.duplicateInvoices[${i}].id`);
          forEach(inv.fields, (field, k) => {
            unset(params.values, `stripe.customFields.duplicateInvoices[${i}].fields[${k}].id`);
          });
        });

        // Remove IDs from default invoice fields
        forEach(get(params.values, 'stripe.customFields.defaultInvoice.fields', []), (inv, i) => {
          unset(params.values, `stripe.customFields.defaultInvoice.fields[${i}].id`);
        });
      }
    }

    try {
      const updatedSettings = await updateCompanySettings(companySettings._id, params);
      dispatch(updateCompanySettingsStore(updatedSettings));
      setIsDirty(false);
      handleResetCommuteDirty();
      Toast({
        type: 'open',
        message: t('settingUpdated'),
      });
    } catch (error) {
      handleApiErrors(error.response, () => {
        Toast({
          type: 'error',
          message: t('errorUpdatingCompanySettings'),
        });
      });
    }

    setIsSubmitting(false);
  }, [
    t,
    dispatch,
    authUser,
    queryClient,
    companySettings._id,
    currentCompany._id,
    currentCompanySettingValues,
    formattedCompanyCommuteSettings,
    currentCompanySettings,
    closeCommuteConfirmModal,
    handleMileagePurposeUpdate,
    handleResetCommuteDirty,
  ]);

  return {
    // Boolean
    isSubmitting,
    isCommuteConfirmModalVisible,
    isDirty: isDirty || isCommuteSettingsDirty,
    isCommuteSettingsDirty,

    // General
    authUser,
    currentCompany,
    currentCompanySettings,
    companyCommuteSettings,
    currentCompanySettingValues,

    isCommuteSettingCustom,
    isCommuteSettingSingleLocation,
    isCommuteSettingNotHaveOffice,
    isNoOfficeMaxDeduction,
    isHomeAddressMaxCommuteDeduction,
    commuteDistanceUnit,
    // Handlers
    handleFormSave,
    handleActiveFeatureChange,
    handleSettingValueChange,
    handleCommuteSettingChange,

    openCommuteConfirmModal,
    closeCommuteConfirmModal,
  };
};

export default useCompanySettingsState;
